// The Description page's editor of one target of its lists (component/targetEntries.ts): a word's characters, pinyin and
// meaning, or a grammar point's pattern, explanation (one line at first, growing as it is written) and example, the
// fields the PDF popup's Vocabulary and Grammar steps have. The change is the list's, saved with the lesson.
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { samePoint } from './pdfImport/pdfTargets';
import type { GrammarEntry, VocabularyEntry } from './targetEntries';
import { editablePinyin, pinyinWords } from './studentInstructionalPinyin';
import { VerbaScholaPinyinField } from './VerbaScholaPinyinField';
import { INTER, SLATE } from '../style/verbaScholaStyles';

type VocabularyFields = Pick<VocabularyEntry, 'hanzi' | 'pinyin' | 'meaning'>;
type GrammarFields = Pick<GrammarEntry, 'pattern' | 'meaning' | 'example'>;

/** Says whether the editor holds changes not yet Done, while it is open, so the page keeps them (no refresh replaces the
 * lesson under it, and leaving asks first). */
function useUnsaved(changed: boolean, onUnsavedChange?: (unsaved: boolean) => void) {
    React.useEffect(() => { onUnsavedChange?.(changed); }, [changed, onUnsavedChange]);
    React.useEffect(() => () => onUnsavedChange?.(false), [onUnsavedChange]);
}

/** ``title`` names the target ("word 2"), or is "Add a word" for a new one; ``others`` are the list's other targets, which
 * the lesson holds once each. */
export function VocabularyEntryDialog({ title, entry, others, onDone, onClose, onUnsavedChange }: {
    title: string; entry: VocabularyFields; others: string[]; onDone: (fields: VocabularyFields) => void; onClose: () => void;
    onUnsavedChange?: (unsaved: boolean) => void;
}) {
    // An entry may hold marks as typed or pasted: it opens with tone numbers, which is not a change to it.
    const opened = { ...entry, pinyin: editablePinyin(pinyinWords(entry.pinyin)) };
    const [fields, setFields] = React.useState(opened);
    const unsaved = fields.hanzi !== opened.hanzi || fields.pinyin !== opened.pinyin || fields.meaning !== opened.meaning;
    useUnsaved(unsaved, onUnsavedChange);
    const word = fields.hanzi.trim();
    const listed = Boolean(word) && others.some(other => other.trim() === word);
    const [suggesting, setSuggesting] = React.useState(false);
    return (
        <EntryDialog title={title} ready={Boolean(word) && !listed && !suggesting} changed={unsaved} onClose={onClose} onDone={() => onDone({ ...fields, hanzi: word })}>
            <TextField label="Characters" value={fields.hanzi} autoFocus error={listed} helperText={listed ? `${word} is already in the list.` : undefined}
                onChange={event => { const hanzi = event.target.value; setFields(current => ({ ...current, hanzi })); }} />
            <VerbaScholaPinyinField hanzi={fields.hanzi} value={fields.pinyin} onChange={pinyin => setFields(current => ({ ...current, pinyin }))} onPendingChange={setSuggesting} />
            <TextField label="Meaning" value={fields.meaning} onChange={event => { const meaning = event.target.value; setFields(current => ({ ...current, meaning })); }} />
        </EntryDialog>
    );
}

export function GrammarEntryDialog({ title, entry, others, onDone, onClose, onUnsavedChange }: {
    title: string; entry: GrammarFields; others: string[]; onDone: (fields: GrammarFields) => void; onClose: () => void;
    onUnsavedChange?: (unsaved: boolean) => void;
}) {
    const [fields, setFields] = React.useState(entry);
    const unsaved = fields.pattern !== entry.pattern || fields.meaning !== entry.meaning || fields.example !== entry.example;
    useUnsaved(unsaved, onUnsavedChange);
    const pattern = fields.pattern.trim();
    const listed = Boolean(pattern) && others.some(other => samePoint(other, pattern));
    return (
        <EntryDialog title={title} ready={Boolean(pattern) && !listed} changed={unsaved} onClose={onClose} onDone={() => onDone({ ...fields, pattern })}>
            <TextField label="Pattern" value={fields.pattern} autoFocus error={listed} helperText={listed ? `${pattern} is already in the list.` : undefined}
                onChange={event => { const value = event.target.value; setFields(current => ({ ...current, pattern: value })); }} />
            <TextField label="Explanation" value={fields.meaning} multiline maxRows={8}
                onChange={event => { const meaning = event.target.value; setFields(current => ({ ...current, meaning })); }} />
            <TextField label="Example" value={fields.example} onChange={event => { const example = event.target.value; setFields(current => ({ ...current, example })); }} />
        </EntryDialog>
    );
}

function EntryDialog({ title, ready, changed, onDone, onClose, children }: { title: string; ready: boolean; changed: boolean; onDone: () => void; onClose: () => void; children: React.ReactNode }) {
    return (
        // With changes, a click outside or Escape does not close it: Cancel discards them, Done keeps them.
        <Dialog open onClose={changed ? undefined : onClose} fullWidth maxWidth="sm" aria-label={title} PaperProps={{ sx: { borderRadius: '20px' } }}>
            <DialogTitle sx={{ fontFamily: INTER, fontWeight: 700 }}>{title}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>{children}</Stack>
                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 1.5 }}>Saved with the lesson.</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" disabled={!ready} onClick={onDone} sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Done</Button>
            </DialogActions>
        </Dialog>
    );
}
